import React, { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { UploadCloud, Loader2, File as FileIcon, Trash2, ExternalLink } from 'lucide-react';

function humanSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1024/1024).toFixed(1)} MB`;
}

export default function FileUploader({
  entityType,
  entityId,
  label = 'resume',
  files = [],
  accept = '.pdf,.png,.jpg,.jpeg,.docx,.doc,.txt,.md',
  buttonText = 'Upload',
  onUploaded,
  onDeleted,
  compact = false,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entity_type', entityType);
      fd.append('entity_id', entityId);
      fd.append('label', label);
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded');
      onUploaded && onUploaded(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const del = async (fid) => {
    if (!window.confirm('Delete this file?')) return;
    try { await api.delete(`/files/${fid}`); toast.success('Deleted'); onDeleted && onDeleted(fid); }
    catch (e) { toast.error('Delete failed'); }
  };

  const openFile = (fid) => {
    const token = localStorage.getItem('hr_token');
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}/api/files/${fid}/download?auth=${token}`, '_blank');
  };

  return (
    <div className={compact ? '' : 'space-y-2'}>
      <div className="flex items-center gap-2 flex-wrap">
        <input ref={inputRef} type="file" accept={accept} onChange={(e) => upload(e.target.files[0])} className="hidden" data-testid={`file-input-${label}`} />
        <Button size={compact ? 'sm' : 'default'} variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`file-upload-btn-${label}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5 mr-1" />} {buttonText}
        </Button>
        <span className="text-xs text-muted-foreground">PDF/DOCX/Images, max 15MB</span>
      </div>
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between rounded border bg-muted/30 px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm truncate">{f.original_filename || f.filename}</p>
                  <p className="text-[11px] text-muted-foreground">{f.label || label} · {humanSize(f.size)}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => openFile(f.id)} data-testid={`file-open-${f.id}`}><ExternalLink className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(f.id)} data-testid={`file-delete-${f.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
